import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  canUseWebLLM,
  ensureWebLLMEngine,
  generateWebLLMReply,
  getWebLLMModelLabel,
  subscribeToWebLLMProgress,
} from '../services/webllmService';

const THERAPIES = [
  { id: 1, label: 'Paclitaxel + Trastuzumab', cancer: 'Breast Cancer', stage: 'Stage II' },
  { id: 2, label: 'Tamoxifen', cancer: 'Breast Cancer', stage: 'Stage I-II' },
  { id: 3, label: 'Anastrozole', cancer: 'Breast Cancer', stage: 'Stage I-III' },
  { id: 4, label: 'Letrozole', cancer: 'Breast Cancer', stage: 'Stage I-III' },
  { id: 5, label: 'Fulvestrant', cancer: 'Breast Cancer', stage: 'Stage IV' },
  { id: 6, label: 'Palbociclib', cancer: 'Breast Cancer', stage: 'Stage IV' },
  { id: 7, label: 'FOLFOX', cancer: 'Colorectal Cancer', stage: 'Stage III' },
  { id: 8, label: 'Capecitabine', cancer: 'Colorectal Cancer', stage: 'Stage II-III' },
  { id: 9, label: 'Cetuximab', cancer: 'Colorectal Cancer', stage: 'Stage IV' },
  { id: 10, label: 'Bevacizumab', cancer: 'Colorectal Cancer', stage: 'Stage IV' },
  { id: 11, label: 'Regorafenib', cancer: 'Colorectal Cancer', stage: 'Stage IV' },
  { id: 12, label: 'Pembrolizumab', cancer: 'Lung Cancer', stage: 'Stage IV' },
  { id: 13, label: 'Osimertinib', cancer: 'Lung Cancer', stage: 'Stage IV' },
  { id: 14, label: 'Atezolizumab', cancer: 'Lung Cancer', stage: 'Stage IV' },
  { id: 15, label: 'Crizotinib', cancer: 'Lung Cancer', stage: 'Stage IV' },
  { id: 16, label: 'Docetaxel', cancer: 'Lung Cancer', stage: 'Stage III-IV' },
  { id: 17, label: 'Docetaxel', cancer: 'Prostate Cancer', stage: 'Stage IV' },
  { id: 18, label: 'Abiraterone', cancer: 'Prostate Cancer', stage: 'Stage IV' },
  { id: 19, label: 'Enzalutamide', cancer: 'Prostate Cancer', stage: 'Stage IV' },
  { id: 20, label: 'Leuprolide', cancer: 'Prostate Cancer', stage: 'Stage II-IV' },
  { id: 21, label: 'Gemcitabine', cancer: 'Pancreatic Cancer', stage: 'Stage II-IV' },
  { id: 22, label: 'FOLFIRINOX', cancer: 'Pancreatic Cancer', stage: 'Stage II-III' },
  { id: 23, label: 'Nab-paclitaxel', cancer: 'Pancreatic Cancer', stage: 'Stage IV' },
  { id: 24, label: 'Carboplatin + Paclitaxel', cancer: 'Ovarian Cancer', stage: 'Stage III-IV' },
  { id: 25, label: 'Bevacizumab', cancer: 'Ovarian Cancer', stage: 'Stage III-IV' },
  { id: 26, label: 'Olaparib', cancer: 'Ovarian Cancer', stage: 'Stage IV' },
  { id: 27, label: 'Cisplatin + Gemcitabine', cancer: 'Bladder Cancer', stage: 'Stage IV' },
  { id: 28, label: 'Atezolizumab', cancer: 'Bladder Cancer', stage: 'Stage IV' },
  { id: 29, label: 'Pembrolizumab', cancer: 'Bladder Cancer', stage: 'Stage IV' },
  { id: 30, label: 'Sunitinib', cancer: 'Kidney Cancer', stage: 'Stage IV' },
  { id: 31, label: 'Pazopanib', cancer: 'Kidney Cancer', stage: 'Stage IV' },
  { id: 32, label: 'Nivolumab', cancer: 'Kidney Cancer', stage: 'Stage IV' },
  { id: 33, label: 'Sorafenib', cancer: 'Liver Cancer', stage: 'Stage IV' },
  { id: 34, label: 'Lenvatinib', cancer: 'Liver Cancer', stage: 'Stage IV' },
  { id: 35, label: 'Atezolizumab + Bevacizumab', cancer: 'Liver Cancer', stage: 'Stage IV' },
  { id: 36, label: 'Trastuzumab', cancer: 'Stomach Cancer', stage: 'Stage IV' },
  { id: 37, label: 'Ramucirumab', cancer: 'Stomach Cancer', stage: 'Stage IV' },
  { id: 38, label: 'FOLFOX', cancer: 'Stomach Cancer', stage: 'Stage II-III' },
  { id: 39, label: 'Cisplatin + Fluorouracil', cancer: 'Esophageal Cancer', stage: 'Stage II-III' },
  { id: 40, label: 'Docetaxel', cancer: 'Esophageal Cancer', stage: 'Stage IV' },
  { id: 41, label: 'Cisplatin', cancer: 'Head and Neck Cancer', stage: 'Stage III-IV' },
  { id: 42, label: 'Cetuximab', cancer: 'Head and Neck Cancer', stage: 'Stage III-IV' },
  { id: 43, label: 'Pembrolizumab', cancer: 'Head and Neck Cancer', stage: 'Stage IV' },
  { id: 44, label: 'Dabrafenib + Trametinib', cancer: 'Melanoma', stage: 'Stage IV' },
  { id: 45, label: 'Nivolumab', cancer: 'Melanoma', stage: 'Stage IV' },
  { id: 46, label: 'Ipilimumab', cancer: 'Melanoma', stage: 'Stage IV' },
  { id: 47, label: 'Imatinib', cancer: 'Chronic Myeloid Leukemia', stage: 'All Stages' },
  { id: 48, label: 'Dasatinib', cancer: 'Chronic Myeloid Leukemia', stage: 'All Stages' },
  { id: 49, label: 'Venetoclax', cancer: 'Acute Myeloid Leukemia', stage: 'All Stages' },
  { id: 50, label: 'Rituximab + CHOP', cancer: 'Non-Hodgkin Lymphoma', stage: 'Stage III-IV' },
  { id: 51, label: 'Brentuximab', cancer: 'Hodgkin Lymphoma', stage: 'Stage III-IV' },
  { id: 52, label: 'Ibrutinib', cancer: 'Non-Hodgkin Lymphoma', stage: 'Stage IV' },
  { id: 53, label: 'Bortezomib', cancer: 'Multiple Myeloma', stage: 'All Stages' },
  { id: 54, label: 'Lenalidomide', cancer: 'Multiple Myeloma', stage: 'All Stages' },
  { id: 55, label: 'Daratumumab', cancer: 'Multiple Myeloma', stage: 'All Stages' },
  { id: 56, label: 'Sorafenib', cancer: 'Thyroid Cancer', stage: 'Stage IV' },
  { id: 57, label: 'Lenvatinib', cancer: 'Thyroid Cancer', stage: 'Stage IV' },
  { id: 58, label: 'Temozolomide', cancer: 'Glioblastoma', stage: 'Stage IV' },
  { id: 59, label: 'Bevacizumab', cancer: 'Glioblastoma', stage: 'Stage IV' },
  { id: 60, label: 'Cisplatin', cancer: 'Cervical Cancer', stage: 'Stage II-IV' },
  { id: 61, label: 'Bevacizumab', cancer: 'Cervical Cancer', stage: 'Stage IV' },
  { id: 62, label: 'Carboplatin + Paclitaxel', cancer: 'Endometrial Cancer', stage: 'Stage III-IV' },
  { id: 63, label: 'Pembrolizumab', cancer: 'Endometrial Cancer', stage: 'Stage IV' },
];

function formatProgress(progress) {
  if (!progress) {
    return 'Preparing local AI model...';
  }

  const label =
    progress.text ||
    progress.status ||
    progress.type ||
    'Preparing local AI model...';

  if (typeof progress.progress === 'number') {
    return `${label} (${Math.round(progress.progress * 100)}%)`;
  }

  return label;
}

function buildSystemPrompt(userProfile, selectedTherapy) {
  const profileLines = [
    userProfile?.cancerType ? `Cancer type: ${userProfile.cancerType}` : null,
    userProfile?.diseaseStage ? `Disease stage: ${userProfile.diseaseStage}` : null,
    selectedTherapy?.label ? `Selected treatment: ${selectedTherapy.label}` : null,
    selectedTherapy?.stage ? `Treatment stage context: ${selectedTherapy.stage}` : null,
  ].filter(Boolean);

  return [
    'You are Reaxano, a compassionate oncology support assistant for people receiving cancer care in Ontario.',
    'Help users think through whether symptoms may be related to treatment side effects, offer practical self-care suggestions, and explain when they should contact their oncology team promptly.',
    'You are not a clinician, you do not diagnose, and you should clearly recommend urgent care when symptoms sound severe or dangerous.',
    'Keep responses supportive, medically cautious, and easy to understand.',
    profileLines.length > 0 ? `Patient context:\n${profileLines.join('\n')}` : null,
  ]
    .filter(Boolean)
    .join('\n\n');
}

export default function PatientChat({ userProfile }) {
  const [selectedTherapy, setSelectedTherapy] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [engineReady, setEngineReady] = useState(false);
  const [engineStatus, setEngineStatus] = useState('Waiting to load local AI model...');
  const [engineError, setEngineError] = useState('');
  const bottomRef = useRef(null);

  const availableTherapies = THERAPIES.filter(
    (therapy) => therapy.cancer === userProfile?.cancerType,
  );

  useEffect(() => {
    if (selectedTherapy) {
      setMessages([
        {
          role: 'assistant',
          text: `Hi, I'm Reaxano. I can help you think through whether a symptom may be related to ${selectedTherapy.label}. What symptom are you noticing?`,
        },
      ]);
    }
  }, [selectedTherapy]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const unsubscribe = subscribeToWebLLMProgress((progress) => {
      setEngineStatus(formatProgress(progress));
    });

    return unsubscribe;
  }, []);

  const initializeEngine = useCallback(async () => {
    if (engineReady) {
      return true;
    }

    if (!canUseWebLLM()) {
      setEngineError(
        'This browser does not support WebGPU. Try recent Chrome or Edge to use local in-browser AI.',
      );
      setEngineStatus('WebGPU not available in this browser.');
      return false;
    }

    setEngineError('');
    setEngineStatus('Preparing local AI model...');

    try {
      await ensureWebLLMEngine();
      const modelLabel = getWebLLMModelLabel();
      setEngineReady(true);
      setEngineStatus(
        modelLabel ? `Local AI ready: ${modelLabel}` : 'Local AI model is ready.',
      );
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to initialize the local AI model.';
      setEngineError(message);
      setEngineStatus('Local AI model failed to load.');
      return false;
    }
  }, [engineReady]);

  useEffect(() => {
    if (selectedTherapy) {
      void initializeEngine();
    }
  }, [initializeEngine, selectedTherapy]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !selectedTherapy || loading) {
      return;
    }

    const isReady = await initializeEngine();
    if (!isReady) {
      return;
    }

    const userMsg = { role: 'user', text };
    setMessages((prev) => [...prev, userMsg, { role: 'assistant', text: '' }]);
    setInput('');
    setLoading(true);

    try {
      const conversation = [
        {
          role: 'system',
          content: buildSystemPrompt(userProfile, selectedTherapy),
        },
        ...messages
          .slice(-8)
          .filter((message) => message?.text)
          .map((message) => ({
            role: message.role,
            content: message.text,
          })),
        { role: 'user', content: text },
      ];

      const reply = await generateWebLLMReply({
        messages: conversation,
        onChunk: (partialReply) => {
          setMessages((prev) => {
            const next = [...prev];
            const lastIndex = next.length - 1;
            if (lastIndex >= 0) {
              next[lastIndex] = { role: 'assistant', text: partialReply };
            }
            return next;
          });
        },
      });

      if (!reply) {
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: 'assistant',
            text: 'Sorry, I had trouble responding. Please contact your care team if this symptom is concerning.',
          };
          return next;
        });
      }
    } catch (error) {
      const fallbackMessage =
        error instanceof Error && error.message
          ? `${error.message} If this symptom feels serious, contact your care team today.`
          : 'The local AI model had trouble responding. If this symptom feels serious, contact your care team today.';

      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: 'assistant',
          text: fallbackMessage,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile) {
    return <div>Loading...</div>;
  }

  const inputDisabled = !selectedTherapy || !engineReady || Boolean(engineError);

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '12px' }}>
          <strong>{userProfile.cancerType}</strong> - {userProfile.diseaseStage}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#666',
              marginBottom: '8px',
              display: 'block',
            }}
          >
            Select Your Treatment:
          </label>
          <select
            value={selectedTherapy?.id || ''}
            onChange={(event) => {
              const therapyId = parseInt(event.target.value, 10);
              const therapy = availableTherapies.find((item) => item.id === therapyId);
              setSelectedTherapy(therapy || null);
            }}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '14px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              width: '100%',
              maxWidth: '400px',
            }}
          >
            <option value="">Choose a treatment...</option>
            {availableTherapies.map((therapy) => (
              <option key={therapy.id} value={therapy.id}>
                {therapy.label} ({therapy.stage})
              </option>
            ))}
          </select>
        </div>

        <div
          style={{
            fontSize: '13px',
            color: engineError ? '#9B1C1C' : 'var(--teal-dark)',
            background: engineError ? '#FEE2E2' : 'var(--brand-primary-light)',
            border: `1px solid ${engineError ? '#FECACA' : 'var(--brand-primary-border)'}`,
            borderRadius: '10px',
            padding: '10px 12px',
            maxWidth: '640px',
          }}
        >
          {engineError || engineStatus}
        </div>
      </div>

      <div
        style={{
          minHeight: '350px',
          border: '1px solid #ddd',
          borderRadius: '12px',
          padding: '16px',
          background: 'white',
          marginBottom: '12px',
        }}
      >
        {selectedTherapy ? (
          <>
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                style={{
                  marginBottom: '12px',
                  textAlign: message.role === 'user' ? 'right' : 'left',
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    background: message.role === 'user' ? 'var(--brand-primary-light)' : '#f4f4f4',
                    maxWidth: '92%',
                  }}
                >
                  {message.role === 'assistant' ? (
                    <ReactMarkdown>{message.text || '...'}</ReactMarkdown>
                  ) : (
                    message.text
                  )}
                </div>
              </div>
            ))}

            {loading && <div>Reaxano is thinking locally on your device...</div>}
            <div ref={bottomRef} />
          </>
        ) : (
          <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            Please select a treatment above to start chatting with Reaxano.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && sendMessage()}
          placeholder={
            selectedTherapy
              ? 'Describe your symptom...'
              : 'Select a treatment first'
          }
          disabled={inputDisabled}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #ccc',
            opacity: inputDisabled ? 0.5 : 1,
          }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || inputDisabled}
          style={{
            padding: '12px 18px',
            borderRadius: '8px',
            border: 'none',
            background: loading || inputDisabled ? '#ccc' : 'var(--teal)',
            color: 'white',
            cursor: loading || inputDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Send
        </button>
      </div>

      <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
        Reaxano supports but does not replace your oncology care team.
      </p>
    </div>
  );
}
